'use client';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Search, SlidersHorizontal, Star, Tv, ChevronLeft, ChevronRight, X, Loader2, CheckCircle2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

const ITEMS_PER_PAGE = 12;
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'recent', label: 'Recently Added' },
  { value: 'trending', label: 'Trending' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'title', label: 'A–Z' },
];

const LANGUAGE_OPTIONS = [
  'English', 'Hindi', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
  'Japanese', 'Korean', 'Chinese', 'Arabic', 'Russian', 'Turkish', 'Thai',
];

const COUNTRY_OPTIONS = [
  'USA', 'UK', 'India', 'France', 'Germany', 'Spain', 'Italy', 'Japan',
  'South Korea', 'China', 'Australia', 'Canada', 'Brazil', 'Mexico', 'Turkey',
];

interface Series {
  id: string;
  title: string;
  slug: string;
  description: string;
  year: number;
  imdb_rating: number;
  genre: string;
  poster_url: string;
  season_count: number;
  featured: boolean;
  created_at: string;
  language?: string;
  country?: string;
  download_count?: number;
  isCompleted?: boolean;
}

export default function SeriesGrid() {
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [ratingMin, setRatingMin] = useState('');
  const [ratingMax, setRatingMax] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const supabase = createClient();

  const fetchSeries = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('series')
        .select('id, title, slug, description, year, imdb_rating, genre, poster_url, season_count, featured, created_at, language, country, download_count')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching series:', error.message);
      } else {
        const fetchedSeries = data || [];
        // Fetch completion status for all series
        const seriesIds = fetchedSeries.map((s) => s.id);
        const completionMap: Record<string, boolean> = {};

        if (seriesIds.length > 0) {
          const { data: episodesData } = await supabase
            .from('episodes')
            .select('series_id, is_completed')
            .in('series_id', seriesIds);

          if (episodesData) {
            seriesIds.forEach((sid) => {
              const eps = episodesData.filter((e) => e.series_id === sid);
              completionMap[sid] = eps.length > 0 && eps.every((e) => e.is_completed === true);
            });
          }
        }

        setSeries(fetchedSeries.map((s) => ({ ...s, isCompleted: completionMap[s.id] ?? false })));
      }
    } catch (err: any) {
      console.error('Fetch series error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  useRealtimeContent({
    tables: ['series'],
    onAnyChange: () => fetchSeries(),
  });

  const genres = useMemo(() => [...new Set(series.map((s) => s.genre).filter(Boolean))].sort(), [series]);
  const years = useMemo(() => [...new Set(series.map((s) => s.year))].sort((a, b) => b - a), [series]);
  const languages = useMemo(() => {
    const fromData = [...new Set(series.map((s) => s.language).filter(Boolean))] as string[];
    const merged = [...new Set([...fromData, ...LANGUAGE_OPTIONS])].sort();
    return merged;
  }, [series]);
  const countries = useMemo(() => {
    const fromData = [...new Set(series.map((s) => s.country).filter(Boolean))] as string[];
    const merged = [...new Set([...fromData, ...COUNTRY_OPTIONS])].sort();
    return merged;
  }, [series]);

  const filtered = useMemo(() => {
    let list = [...series];
    if (search) list = list.filter((s) => s.title?.toLowerCase().includes(search.toLowerCase()));
    if (selectedGenre) list = list.filter((s) => s.genre === selectedGenre);
    if (selectedYear) list = list.filter((s) => s.year === Number(selectedYear));
    if (selectedLanguage) list = list.filter((s) => s.language === selectedLanguage);
    if (selectedCountry) list = list.filter((s) => s.country === selectedCountry);
    if (ratingMin !== '') list = list.filter((s) => (s.imdb_rating ?? 0) >= Number(ratingMin));
    if (ratingMax !== '') list = list.filter((s) => (s.imdb_rating ?? 0) <= Number(ratingMax));
    switch (sortBy) {
      case 'trending': list.sort((a, b) => (b.download_count ?? 0) - (a.download_count ?? 0)); break;
      case 'rating': list.sort((a, b) => (b.imdb_rating ?? 0) - (a.imdb_rating ?? 0)); break;
      case 'title': list.sort((a, b) => a.title?.localeCompare(b.title)); break;
      case 'recent':
      default: list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return list;
  }, [series, search, selectedGenre, selectedYear, selectedLanguage, selectedCountry, ratingMin, ratingMax, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const hasFilters = !!(search || selectedGenre || selectedYear || selectedLanguage || selectedCountry || ratingMin || ratingMax || sortBy !== 'newest');

  const clearFilters = () => {
    setSearch('');
    setSelectedGenre('');
    setSelectedYear('');
    setSelectedLanguage('');
    setSelectedCountry('');
    setRatingMin('');
    setRatingMax('');
    setSortBy('newest');
    setPage(1);
  };

  const activeFilterCount = [selectedGenre, selectedYear, selectedLanguage, selectedCountry, ratingMin || ratingMax].filter(Boolean).length;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gradient-purple mb-1">Series</h1>
        <p className="text-muted-foreground font-body text-sm">
          {loading ? 'Loading...' : `${filtered.length} series available`}
        </p>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search series..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full neon-input rounded-xl pl-9 pr-4 py-2.5 text-sm"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="neon-input rounded-xl px-3 py-2.5 text-sm cursor-pointer"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={`sort-${o.value}`} value={o.value} className="bg-secondary text-foreground">
                {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border ${
              filtersOpen || hasFilters
                ? 'border-accent/50 text-accent bg-accent/10' : 'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
            }`}
          >
            <SlidersHorizontal size={15} />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-accent text-[10px] font-bold text-background flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2.5 rounded-xl text-xs font-semibold text-muted-foreground hover:text-foreground border border-border hover:border-border/60 transition-all"
            >
              <X size={13} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="glass-card rounded-xl p-5 mb-6 border border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Genre */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Genre</label>
              <div className="flex flex-wrap gap-2">
                {['', ...genres].map((g) => (
                  <button
                    key={`genre-filter-${g || 'all'}`}
                    onClick={() => { setSelectedGenre(g); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
                      selectedGenre === g
                        ? 'border-accent/50 text-accent bg-accent/10' : 'border-border text-muted-foreground hover:border-border/60 hover:text-foreground'
                    }`}
                  >
                    {g || 'All Genres'}
                  </button>
                ))}
              </div>
            </div>

            {/* Year */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Year</label>
              <div className="flex flex-wrap gap-2">
                {['', ...years.map(String)].map((y) => (
                  <button
                    key={`year-filter-${y || 'all'}`}
                    onClick={() => { setSelectedYear(y); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold font-mono-data transition-all duration-200 border ${
                      selectedYear === y
                        ? 'border-accent/50 text-accent bg-accent/10' : 'border-border text-muted-foreground hover:border-border/60 hover:text-foreground'
                    }`}
                  >
                    {y || 'All Years'}
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Language</label>
              <select
                value={selectedLanguage}
                onChange={(e) => { setSelectedLanguage(e.target.value); setPage(1); }}
                className="w-full neon-input rounded-xl px-3 py-2.5 text-sm cursor-pointer"
              >
                <option value="" className="bg-secondary text-foreground">All Languages</option>
                {languages.map((lang) => (
                  <option key={`lang-${lang}`} value={lang} className="bg-secondary text-foreground">{lang}</option>
                ))}
              </select>
            </div>

            {/* Country */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Country</label>
              <select
                value={selectedCountry}
                onChange={(e) => { setSelectedCountry(e.target.value); setPage(1); }}
                className="w-full neon-input rounded-xl px-3 py-2.5 text-sm cursor-pointer"
              >
                <option value="" className="bg-secondary text-foreground">All Countries</option>
                {countries.map((c) => (
                  <option key={`country-${c}`} value={c} className="bg-secondary text-foreground">{c}</option>
                ))}
              </select>
            </div>

            {/* Rating Range */}
            <div className="sm:col-span-2 lg:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Rating Range
                {(ratingMin || ratingMax) && (
                  <span className="ml-2 text-accent normal-case font-normal">
                    {ratingMin || '0'} – {ratingMax || '10'}
                  </span>
                )}
              </label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-muted-foreground">Min</span>
                    <span className="text-[10px] font-mono-data text-accent">{ratingMin || '0'}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={ratingMin || '0'}
                    onChange={(e) => { setRatingMin(e.target.value === '0' ? '' : e.target.value); setPage(1); }}
                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-purple-500 bg-border"
                  />
                </div>
                <span className="text-muted-foreground text-xs">–</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-muted-foreground">Max</span>
                    <span className="text-[10px] font-mono-data text-accent">{ratingMax || '10'}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={ratingMax || '10'}
                    onChange={(e) => { setRatingMax(e.target.value === '10' ? '' : e.target.value); setPage(1); }}
                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-purple-500 bg-border"
                  />
                </div>
                <div className="flex gap-1.5">
                  {['6', '7', '8'].map((r) => (
                    <button
                      key={`rating-preset-${r}`}
                      onClick={() => { setRatingMin(r); setRatingMax(''); setPage(1); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                        ratingMin === r && !ratingMax
                          ? 'border-accent/50 text-accent bg-accent/10' : 'border-border text-muted-foreground hover:border-border/60 hover:text-foreground'
                      }`}
                    >
                      {r}+
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Genre quick chips */}
      {genres.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
          {['All', ...genres].map((g) => (
            <button
              key={`quick-genre-${g}`}
              onClick={() => { setSelectedGenre(g === 'All' ? '' : g); setPage(1); }}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
                (g === 'All' && !selectedGenre) || selectedGenre === g
                  ? 'border-accent/50 text-accent bg-accent/10' : 'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="glass-card border border-border rounded-2xl p-16 flex flex-col items-center justify-center gap-4">
          <Loader2 size={32} className="animate-spin text-accent" />
          <p className="text-sm text-muted-foreground font-body">Loading series...</p>
        </div>
      ) : paginated.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center border border-border">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Tv size={28} className="text-accent" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            {series.length === 0 ? 'No series yet' : 'No series found'}
          </h3>
          <p className="text-sm text-muted-foreground font-body mb-6">
            {series.length === 0
              ? 'Series added from the admin dashboard will appear here.' :'No series match your current filters. Try adjusting your search.'}
          </p>
          {hasFilters && (
            <button onClick={clearFilters} className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-accent/50 text-accent hover:bg-accent/10 transition-all">
              Clear All Filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-8">
          {paginated.map((s) => (
            <Link key={`series-grid-${s.id}`} href={`/series/${s.slug}`} className="block group">
              <div className="glass-card glass-card-hover rounded-xl overflow-hidden cursor-pointer">
                <div className="relative aspect-[2/3] overflow-hidden">
                  <AppImage
                    src={s.poster_url || '/assets/images/no_image.png'}
                    alt={`${s.title} — ${s.genre} series`}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 20vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-70" />
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                    <Star size={10} className="rating-star fill-yellow-400" />
                    <span className="text-xs font-bold font-mono-data">{s.imdb_rating?.toFixed(1) ?? '—'}</span>
                  </div>
                  {s.season_count > 0 && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                      <Tv size={10} className="text-accent" />
                      <span className="text-[10px] font-bold text-accent">{s.season_count}S</span>
                    </div>
                  )}
                  {s.language && (
                    <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5">
                      <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide">{s.language}</span>
                    </div>
                  )}
                  {s.featured && (
                    <div className="absolute top-2 left-2 bg-accent/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                      FEATURED
                    </div>
                  )}
                  {/* Completed overlay badge */}
                  {s.isCompleted && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[1px]">
                      <div className="flex flex-col items-center gap-1.5 bg-green-500/90 rounded-xl px-3 py-2 shadow-lg">
                        <CheckCircle2 size={20} className="text-white" />
                        <span className="text-[11px] font-extrabold text-white tracking-wider uppercase">Completed</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-accent transition-colors">
                    {s.title}
                  </h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground font-mono-data">{s.year}</span>
                    <span className="genre-badge" style={{ borderColor: 'rgba(179,71,255,0.25)', color: 'var(--neon-purple)', background: 'rgba(179,71,255,0.1)' }}>
                      {s.genre}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pb-4">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-accent hover:border-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={18} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={`page-${p}`}
              onClick={() => setPage(p)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${
                p === page
                  ? 'bg-accent/20 text-accent border border-accent/40' :'text-muted-foreground hover:text-foreground border border-border hover:border-border/60'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-accent hover:border-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
